import { useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Badge } from '@givebutter/primitives/badge';
import { Button } from '@givebutter/primitives/button';
import * as ButtonGroup from '@givebutter/primitives/button-group';
import * as Draggable from '@givebutter/primitives/draggable';
import { Label } from '@givebutter/primitives/label';
import { Separator } from '@givebutter/primitives/separator';
import { useToast } from '@givebutter/primitives/toast';
import {
    faArrowDown,
    faArrowLeft,
    faArrowUp,
    faCog,
    faPlus,
    faRedo,
    faSave,
    faSidebar,
    faUndo,
} from '@fortawesome/pro-regular-svg-icons';
import {
    faCircleQuestion,
    faPlus as faPlusSolid,
    faTablePicnic,
    faWandSparkles,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { updateSeatingArrangement } from 'api/events';
import { DragOverlay } from 'assets/js/pages/campaign-settings/campaign-event/table-seating/DragOverlay';
import * as SeatingFrame from 'assets/js/pages/campaign-settings/campaign-event/table-seating/SeatingFrame';
import * as SeatingGroupCard from 'assets/js/pages/campaign-settings/campaign-event/table-seating/SeatingGroupCard';
import * as TableLayoutSetupDialog from 'assets/js/pages/campaign-settings/campaign-event/table-seating/TableLayoutSetupDialog';
import {
    SEATED_TAB,
    SeatingGroupType,
    UNSEATED_GROUP_ID,
} from 'assets/js/pages/campaign-settings/campaign-event/table-seating/constants';
import { useInitializeSeating } from 'assets/js/pages/campaign-settings/campaign-event/table-seating/hooks/useInitializeSeating';
import { useTableDragDrop } from 'assets/js/pages/campaign-settings/campaign-event/table-seating/hooks/useTableDragDrop';
import { useTableSeatingStore } from 'assets/js/pages/campaign-settings/campaign-event/table-seating/store';
import { SeatingTicket } from 'assets/js/pages/campaign-settings/campaign-event/table-seating/types';
import { pointerWithin } from 'assets/js/pages/campaign-settings/campaign-event/table-seating/utils/collisionDetection';
import { formatSeatingPayload } from 'assets/js/pages/campaign-settings/campaign-event/table-seating/utils/transformers';
import * as Input from 'components/input';
import { useCurrentCampaignEvent } from 'customHooks/useCurrentCampaignEvent';
import { eventQueryKeys } from 'queries/event-query-keys';
import { queryClient } from 'queries/queryClient';
import { GivebutterAxiosError } from 'types/error';

type SeatingTabType = typeof SEATED_TAB | typeof UNSEATED_GROUP_ID;

const compareBySeatNumber = (a: SeatingTicket, b: SeatingTicket): number =>
    (a.seat_number || 0) - (b.seat_number || 0);

const TableSeatingBuilder = () => {
    const history = useHistory();
    const toast = useToast();
    const { data: event } = useCurrentCampaignEvent();

    const [search, setSearch] = useState('');
    const [activeTab, setActiveTab] = useState<SeatingTabType>(UNSEATED_GROUP_ID);
    const { tickets, seatingGroups } = useInitializeSeating();

    const unseatedTickets = useMemo(() => {
        if (!tickets) return [];
        return Object.values(tickets)
            .filter(ticket => ticket.seating_group_id === UNSEATED_GROUP_ID)
            .sort(compareBySeatNumber);
    }, [tickets]);

    const seatedTickets = useMemo(() => {
        if (!tickets) return [];
        return Object.values(tickets).filter(
            ticket => ticket.seating_group_id && ticket.seating_group_id !== UNSEATED_GROUP_ID,
        );
    }, [tickets]);

    const tables = useMemo(() => {
        if (!seatingGroups) return [];
        return Object.values(seatingGroups)
            .filter(group => group.type === SeatingGroupType.TABLE)
            .sort((a, b) => a.number - b.number);
    }, [seatingGroups]);

    const ticketsByTable = useMemo(() => {
        if (!tickets) return {};

        const ticketEntries = Object.values(tickets).filter(
            ticket => ticket.seating_group_id && ticket.seating_group_id !== UNSEATED_GROUP_ID,
        );

        const grouped = ticketEntries.reduce<Record<string, SeatingTicket[]>>((acc, ticket) => {
            const tableId = ticket.seating_group_id!;
            return {
                ...acc,
                [tableId]: [...(acc[tableId] || []), ticket],
            };
        }, {});

        return Object.fromEntries(
            Object.entries(grouped).map(([tableId, tableTickets]) => [
                tableId,
                [...tableTickets].sort(compareBySeatNumber),
            ]),
        );
    }, [tickets]);

    const unseatedGroup = seatingGroups?.[UNSEATED_GROUP_ID];
    const unseatedCount = unseatedTickets.length;
    const seatedCount = seatedTickets.length;
    const shouldShowSetupDialog = tables.length === 0;

    const { markAsSaved, addSeatingGroup, takeSnapshot, undo, redo, past, future } =
        useTableSeatingStore(state => ({
            markAsSaved: state.markAsSaved,
            addSeatingGroup: state.addSeatingGroup,
            takeSnapshot: state.takeSnapshot,
            undo: state.undo,
            redo: state.redo,
            past: state.past,
            future: state.future,
        }));

    const canUndo = past.length > 0;
    const canRedo = future.length > 0;

    const { mutate: saveSeating, isPending: isSaving } = useMutation({
        mutationFn: async () => {
            const state = useTableSeatingStore.getState();
            const payload = formatSeatingPayload({
                tickets: state.tickets,
                seatingGroups: state.seatingGroups,
                deletedSeatingGroupIds: state.deletedSeatingGroupIds,
            });
            return updateSeatingArrangement(event.id, payload);
        },
        onSuccess: () => {
            markAsSaved();
            queryClient.invalidateQueries({
                queryKey: eventQueryKeys.seatingLayout(event.id),
            });
            queryClient.invalidateQueries({
                queryKey: eventQueryKeys.ticketGroups(event.id),
            });
            toast.success('Seating arrangement saved successfully!');
        },
        onError: (err: GivebutterAxiosError) => {
            toast.error(
                err.response?.data.message ||
                    'Unable to save seating arrangement. Please try again.',
            );
        },
    });

    const { handleDragStart, handleDragEnd, handleDragCancel } = useTableDragDrop();

    return (
        <Draggable.Root
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDragCancel={handleDragCancel}
            collisionDetection={pointerWithin}
            modifiers={[]}
        >
            <SeatingFrame.Root>
                <SeatingFrame.Title>Table Seating Builder</SeatingFrame.Title>
                <SeatingFrame.LeftPanel>
                    <div className="flex h-full w-80 flex-col gap-4 overflow-hidden p-4">
                        <h2 className="text-xl font-medium">Attendees</h2>
                        <div className="flex items-center gap-2">
                            <div className="flex w-full flex-col">
                                <Label className="sr-only" htmlFor="seating_attendees_search">
                                    Search
                                </Label>
                                <Input.Root size="sm">
                                    <Input.SearchIcon />
                                    <Input.Form
                                        id="seating_attendees_search"
                                        value={search}
                                        placeholder={'Search'}
                                        onChange={e => setSearch(e.target.value)}
                                    />
                                </Input.Root>
                            </div>
                            <Button
                                variant="transparent"
                                color="neutral"
                                size="sm"
                                aria-label="Sort order"
                            >
                                <div className="flex gap-0.5">
                                    <FontAwesomeIcon icon={faArrowUp} aria-hidden="true" />
                                    <FontAwesomeIcon icon={faArrowDown} aria-hidden="true" />
                                </div>
                            </Button>
                        </div>
                        <div className="flex min-h-0 flex-1 flex-col gap-4">
                            <ButtonGroup.Root
                                type="single"
                                value={activeTab}
                                onValueChange={(value: SeatingTabType) => setActiveTab(value)}
                            >
                                <ButtonGroup.Item value={UNSEATED_GROUP_ID}>
                                    <div className={'flex items-center gap-1.5'}>
                                        <p className="text-sm font-medium">Unseated</p>
                                        {unseatedCount > 0 && (
                                            <Badge
                                                size="sm"
                                                className="group-radix-state-on:bg-blue-50 group-radix-state-on:text-blue-600 bg-gray-200"
                                            >
                                                {unseatedCount}
                                            </Badge>
                                        )}
                                    </div>
                                </ButtonGroup.Item>
                                <ButtonGroup.Item value={SEATED_TAB}>
                                    <div className={'flex items-center gap-1.5'}>
                                        <p className="text-sm font-medium">Seated</p>
                                        {seatedCount > 0 && (
                                            <Badge
                                                size="sm"
                                                className="group-radix-state-on:bg-blue-50 group-radix-state-on:text-blue-600 bg-gray-200"
                                            >
                                                {seatedCount}
                                            </Badge>
                                        )}
                                    </div>
                                </ButtonGroup.Item>
                            </ButtonGroup.Root>
                            {activeTab === UNSEATED_GROUP_ID && unseatedGroup && (
                                <SeatingGroupCard.Root
                                    group={unseatedGroup}
                                    normalizedId={UNSEATED_GROUP_ID}
                                    className="min-h-0 w-full flex-1 border-0 shadow-none"
                                >
                                    <SeatingGroupCard.Inner className="h-full overflow-y-auto">
                                        <SeatingGroupCard.Seats />
                                        {unseatedTickets.length === 0 && (
                                            <p className="py-8 text-center text-gray-500">
                                                {seatedCount > 0
                                                    ? 'All guests have been seated'
                                                    : 'No guests to seat'}
                                            </p>
                                        )}
                                    </SeatingGroupCard.Inner>
                                </SeatingGroupCard.Root>
                            )}
                            {activeTab === 'seated' && (
                                <div className="min-h-0 flex-1 overflow-y-auto">
                                    {tables.length === 0 ? (
                                        <p className="py-8 text-center text-gray-500">
                                            No tables created yet
                                        </p>
                                    ) : (
                                        <div className="flex flex-col gap-4">
                                            {tables.map(table => {
                                                const tableTickets = ticketsByTable[table.id] || [];
                                                if (tableTickets.length === 0) return null;

                                                return (
                                                    <div
                                                        key={table.id}
                                                        className="flex flex-col gap-3 rounded-lg border border-gray-200 bg-white p-4"
                                                    >
                                                        <div className="flex items-center justify-between">
                                                            <h3 className="font-medium text-gray-900">
                                                                {table.number} - {table.name}
                                                            </h3>
                                                            <span className="text-sm text-gray-500">
                                                                {tableTickets.length} /{' '}
                                                                {table.capacity}
                                                            </span>
                                                        </div>
                                                        <div className="flex flex-col gap-2">
                                                            {tableTickets.map(ticket => (
                                                                <div
                                                                    key={ticket.id}
                                                                    className="flex items-center gap-3 text-sm"
                                                                >
                                                                    <span className="min-w-16 text-gray-500">
                                                                        Seat {ticket.seat_number}:
                                                                    </span>
                                                                    <span className="font-medium">
                                                                        {ticket.first_name}{' '}
                                                                        {ticket.last_name}
                                                                    </span>
                                                                    {ticket.title && (
                                                                        <span className="text-gray-600">
                                                                            ({ticket.title})
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </SeatingFrame.LeftPanel>
                <SeatingFrame.Divider />
                <SeatingFrame.RightPanel>
                    <SeatingFrame.RightHeader>
                        <div className="flex items-center gap-2">
                            <Button
                                onClick={() => history.goBack()}
                                variant="outline"
                                color="neutral"
                                size="sm"
                            >
                                <FontAwesomeIcon
                                    icon={faArrowLeft}
                                    className="text-gray-700"
                                    aria-hidden="true"
                                />
                                <p className="text-sm font-medium text-gray-700">
                                    Return to dashboard
                                </p>
                            </Button>

                            <Button
                                variant="outline"
                                color="neutral"
                                size="sm"
                                shape="square"
                                aria-label="Toggle sidebar"
                            >
                                <FontAwesomeIcon
                                    icon={faSidebar}
                                    className="text-gray-700"
                                    aria-hidden="true"
                                />
                            </Button>

                            <Button
                                variant="outline"
                                color="neutral"
                                size="sm"
                                shape="square"
                                aria-label="Settings"
                            >
                                <FontAwesomeIcon
                                    icon={faCog}
                                    className="text-gray-700"
                                    aria-hidden="true"
                                />
                            </Button>
                        </div>

                        <div className="flex items-center gap-3">
                            <Button variant="outline" color="neutral" size="sm">
                                <FontAwesomeIcon
                                    icon={faWandSparkles}
                                    className="text-brand-violet-primary"
                                    aria-hidden="true"
                                />
                                <span className="text-sm font-medium text-gray-700">Auto seat</span>
                            </Button>

                            <Button
                                variant="outline"
                                color="neutral"
                                size="sm"
                                onClick={() => {
                                    takeSnapshot();
                                    addSeatingGroup();
                                }}
                            >
                                <FontAwesomeIcon
                                    icon={faPlus}
                                    className="text-gray-700"
                                    aria-hidden="true"
                                />
                                <span className="text-sm font-medium text-gray-700">Add table</span>
                            </Button>

                            <div className="flex items-center">
                                <Button
                                    variant="transparent"
                                    color="neutral"
                                    size="sm"
                                    shape="square"
                                    disabled={!canUndo}
                                    onClick={() => undo()}
                                    aria-label="Undo"
                                >
                                    <FontAwesomeIcon
                                        icon={faUndo}
                                        className="text-gray-700"
                                        aria-hidden="true"
                                    />
                                </Button>

                                <Separator orientation="vertical" className="mx-1 h-6" />

                                <Button
                                    variant="transparent"
                                    color="neutral"
                                    size="sm"
                                    shape="square"
                                    disabled={!canRedo}
                                    onClick={() => redo()}
                                    aria-label="Redo"
                                >
                                    <FontAwesomeIcon
                                        icon={faRedo}
                                        className="text-gray-700"
                                        aria-hidden="true"
                                    />
                                </Button>
                            </div>

                            <Button
                                variant="solid"
                                size="sm"
                                onClick={() => saveSeating()}
                                disabled={isSaving}
                            >
                                <FontAwesomeIcon
                                    icon={faSave}
                                    className="text-white"
                                    aria-hidden="true"
                                />
                                <p>{isSaving ? 'Saving...' : 'Save changes'}</p>
                            </Button>

                            <Button
                                variant="transparent"
                                color="neutral"
                                size="sm"
                                aria-label="Help"
                            >
                                <FontAwesomeIcon
                                    size="lg"
                                    icon={faCircleQuestion}
                                    className="text-gray-700"
                                    aria-hidden="true"
                                />
                            </Button>
                        </div>
                    </SeatingFrame.RightHeader>
                    <SeatingFrame.RightContent>
                        <div className="relative h-full overflow-hidden">
                            <div className={`absolute inset-0 -ml-48 overflow-x-auto pl-48`}>
                                <div className="flex h-full gap-6 py-6">
                                    {tables.map(group => (
                                        <SeatingGroupCard.Root
                                            key={group.id}
                                            group={group}
                                            normalizedId={group.id}
                                        >
                                            <SeatingGroupCard.Inner>
                                                <SeatingGroupCard.Header />
                                                <SeatingGroupCard.Seats />
                                                <SeatingGroupCard.Footer />
                                            </SeatingGroupCard.Inner>
                                        </SeatingGroupCard.Root>
                                    ))}
                                    <AddNewSeatingGroup
                                        onClick={() => {
                                            takeSnapshot();
                                            addSeatingGroup();
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    </SeatingFrame.RightContent>
                </SeatingFrame.RightPanel>

                <Draggable.Overlay>
                    <DragOverlay />
                </Draggable.Overlay>

                <TableLayoutSetupDialog.Root
                    open={shouldShowSetupDialog}
                    onOpenChange={() => {
                        if (shouldShowSetupDialog) {
                            history.goBack();
                        }
                    }}
                />
            </SeatingFrame.Root>
        </Draggable.Root>
    );
};

interface AddNewSeatingGroupProps {
    onClick: () => void;
}

const AddNewSeatingGroup = ({ onClick }: AddNewSeatingGroupProps) => (
    <Button
        onClick={onClick}
        variant="none"
        className="h-30 group relative flex min-w-72 max-w-80 flex-shrink-0 items-center justify-center rounded-md bg-gray-100 p-4 transition-colors hover:bg-gray-200"
        aria-label="Add new seating group"
    >
        <div className="relative">
            <FontAwesomeIcon
                icon={faTablePicnic}
                className="text-gray-500 transition-colors group-hover:text-gray-600"
                size="3x"
                aria-hidden="true"
            />
            <FontAwesomeIcon
                icon={faPlusSolid}
                className="absolute -right-4 -top-5 text-gray-500 transition-colors group-hover:text-gray-600"
                size="2x"
                aria-hidden="true"
            />
        </div>
    </Button>
);

export { TableSeatingBuilder };
